/**
 * The player's keyboard, decided once.
 *
 * Every key YouTube has means what it means on YouTube; the player's own tools take the keys that
 * are free. Nothing here fires while the trainer is typing:
 * a comment containing „k" must not pause the film, and one containing „[" must not set a loop.
 */

export type PlayerCommand =
  | { readonly kind: 'togglePlay' }
  | { readonly kind: 'seekBy'; readonly seconds: number }
  | { readonly kind: 'frame'; readonly by: 1 | -1 }
  | { readonly kind: 'rate'; readonly by: 1 | -1 }
  | { readonly kind: 'seekFraction'; readonly fraction: number }
  | { readonly kind: 'seekEdge'; readonly to: 'start' | 'end' }
  | { readonly kind: 'volume'; readonly by: number }
  | { readonly kind: 'toggleMute' }
  | { readonly kind: 'fullscreen' }
  | { readonly kind: 'clip'; readonly by: 1 | -1 }
  | { readonly kind: 'nextUnread' }
  | { readonly kind: 'commentJump'; readonly by: 1 | -1 }
  | { readonly kind: 'compose' }
  | { readonly kind: 'loopFrom' }
  | { readonly kind: 'loopTo' }
  | { readonly kind: 'loopClear' }
  | { readonly kind: 'compare' }
  | { readonly kind: 'zoom' }
  | { readonly kind: 'pan'; readonly dx: -1 | 0 | 1; readonly dy: -1 | 0 | 1 }
  | { readonly kind: 'theatre' }
  | { readonly kind: 'shortcuts' };

/**
 * Where the key was pressed. `text` is a field being typed into; `control` a button or a link,
 * which keeps Space and Enter for itself; `slider` the scrubber, which keeps its arrows, Home and
 * End; `surface` anywhere else in the player.
 */
export type KeyTarget = 'text' | 'control' | 'slider' | 'surface';

export interface PlayerKey {
  readonly key: string;
  readonly shiftKey: boolean;
  readonly ctrlKey: boolean;
  readonly metaKey: boolean;
  readonly altKey: boolean;
  readonly target: KeyTarget;
  /** While the film is zoomed the four arrows pan it, and seeking waits for the zoom to end. */
  readonly zoomed: boolean;
}

const PAN: Readonly<Record<string, { readonly dx: -1 | 0 | 1; readonly dy: -1 | 0 | 1 }>> = {
  ArrowLeft: { dx: -1, dy: 0 },
  ArrowRight: { dx: 1, dy: 0 },
  ArrowUp: { dx: 0, dy: -1 },
  ArrowDown: { dx: 0, dy: 1 },
};

const VOLUME_STEP = 0.05;

export function playerCommand(input: PlayerKey): PlayerCommand | null {
  const { key, shiftKey, ctrlKey, metaKey, altKey, target, zoomed } = input;
  // Ctrl with an arrow moves between spaces on macOS; the system keeps it.
  if (target === 'text' || metaKey || altKey || ctrlKey) return null;

  if (target === 'slider' && ['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(key)) return null;

  if (shiftKey && key === 'ArrowLeft') return { kind: 'commentJump', by: -1 };
  if (shiftKey && key === 'ArrowRight') return { kind: 'commentJump', by: 1 };

  const pan = PAN[key];
  if (zoomed && pan !== undefined) return { kind: 'pan', ...pan };

  switch (key) {
    case ' ':
      return target === 'control' ? null : { kind: 'togglePlay' };
    case 'ArrowLeft':
      return { kind: 'seekBy', seconds: -5 };
    case 'ArrowRight':
      return { kind: 'seekBy', seconds: 5 };
    case 'ArrowUp':
      return { kind: 'volume', by: VOLUME_STEP };
    case 'ArrowDown':
      return { kind: 'volume', by: -VOLUME_STEP };
    case 'Home':
      return { kind: 'seekEdge', to: 'start' };
    case 'End':
      return { kind: 'seekEdge', to: 'end' };
    case ',':
      return { kind: 'frame', by: -1 };
    case '.':
      return { kind: 'frame', by: 1 };
    case '<':
      return { kind: 'rate', by: -1 };
    case '>':
      return { kind: 'rate', by: 1 };
    case '[':
      return { kind: 'loopFrom' };
    case ']':
      return { kind: 'loopTo' };
    case '\\':
      return { kind: 'loopClear' };
    case '?':
      return { kind: 'shortcuts' };
  }

  if (/^[0-9]$/.test(key)) return { kind: 'seekFraction', fraction: Number(key) / 10 };

  const letter = key.toLowerCase();
  if (shiftKey) {
    if (letter === 'n') return { kind: 'clip', by: 1 };
    if (letter === 'p') return { kind: 'clip', by: -1 };
    if (letter === 'u') return { kind: 'nextUnread' };
    return null;
  }
  switch (letter) {
    case 'k':
      return { kind: 'togglePlay' };
    case 'j':
      return { kind: 'seekBy', seconds: -10 };
    case 'l':
      return { kind: 'seekBy', seconds: 10 };
    case 'm':
      return { kind: 'toggleMute' };
    case 'f':
      return { kind: 'fullscreen' };
    case 'c':
      return { kind: 'compose' };
    case 's':
      return { kind: 'compare' };
    case 'z':
      return { kind: 'zoom' };
    case 't':
      return { kind: 'theatre' };
    default:
      return null;
  }
}
